import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValueEvent } from 'framer-motion';
import '../styles/ThiefOfJoySplash.css';
import handsInvertBg from '../assets/hands_invert.png';
import bandPhotoBg from '../assets/IMG_6755.jpg';
import tracklistBg from '../assets/IMG_1310.JPG';

const ThiefOfJoySplash = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [navVisible, setNavVisible] = useState(true);
  
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const lastScrollY = useRef(0);
  
  // Scroll progress
  const { scrollYProgress, scrollY } = useScroll();
  
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Transform progress to width percentage for progress bar
  const progressWidth = useTransform(smoothProgress, [0, 1], ['0%', '100%']);

  // Fixed title fade out on scroll
  const fixedTitleOpacity = useTransform(scrollYProgress, [0, 0.05], [1, 0]);

  // Hero text animations - individual lines fade in/out at different scroll points (no overlap)
  const heroLine1Opacity = useTransform(scrollYProgress, [0, 0.05, 0.12, 0.15], [0, 1, 1, 0]);
  const heroLine2Opacity = useTransform(scrollYProgress, [0.15, 0.18, 0.25, 0.28], [0, 1, 1, 0]);
  const heroLine3Opacity = useTransform(scrollYProgress, [0.28, 0.31, 0.38, 0.41], [0, 1, 1, 0]);
  const heroLine4Opacity = useTransform(scrollYProgress, [0.41, 0.44, 0.51, 0.54], [0, 1, 1, 0]);
  const heroLine5Opacity = useTransform(scrollYProgress, [0.54, 0.57, 0.64, 0.67], [0, 1, 1, 0]);
  const heroLine6Opacity = useTransform(scrollYProgress, [0.67, 0.70, 0.77, 0.80], [0, 1, 1, 0]);
  const heroLine7Opacity = useTransform(scrollYProgress, [0.80, 0.83, 0.90, 0.93], [0, 1, 1, 0]);
  
  // Video animations
  const videoBorderRadius = useTransform(scrollYProgress, [0, 0.2], ['24px', '0px']);
  const videoScale = useTransform(scrollYProgress, [0, 0.3], [1, 1.05]);

  // Parallax Music Video Scroll Animations
  // Honeybee Official MV (first video) - starts earlier
  const honeybeeVideoOpacity = useTransform(
    scrollYProgress,
    [0.15, 0.18, 0.35, 0.40],  // fade in at 18%, fade out at 40%
    [0, 1, 1, 0]
  );
  
  const honeybeeTextOpacity = useTransform(
    scrollYProgress,
    [0.18, 0.22, 0.35, 0.40],
    [0, 1, 1, 0]
  );

  // Honeybee Studio Version (second video) - same duration as first video
  const studioVideoOpacity = useTransform(
    scrollYProgress,
    [0.38, 0.42, 0.62, 0.67],  // fade in at 42%, fade out at 67% (longer)
    [0, 1, 1, 0]
  );
  
  const studioTextOpacity = useTransform(
    scrollYProgress,
    [0.42, 0.46, 0.62, 0.67],
    [0, 1, 1, 0]
  );

  // Hide navbar immediately after leaving top section
  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest < 50) {
      setNavVisible(true);
    } else if (latest > 50 && latest > lastScrollY.current) {
      // Scrolling down past top
      setNavVisible(false);
    } else if (latest < lastScrollY.current) {
      // Scrolling up
      setNavVisible(true);
    }
    lastScrollY.current = latest;
  });

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Audio toggle functions removed - YouTube native controls handle this now
  // This prevents iframe reloads that were causing crashes on mobile

  const tracklist = [
    { 
      number: 1, 
      title: "How You Believe", 
      duration: "3:32", 
      hasVideo: false,
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 2, 
      title: "Stereo", 
      duration: "3:33", 
      hasVideo: false,
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 3, 
      title: "The Hollows", 
      duration: "4:46", 
      hasVideo: false,
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 4, 
      title: "Cobain", 
      duration: "4:04", 
      hasVideo: false,
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 5, 
      title: "Honeybee", 
      duration: "3:41", 
      hasVideo: true,
      youtubeUrl: "https://www.youtube.com/watch?v=IYLuPQDIDK8",
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 6, 
      title: "Statistics", 
      duration: "3:36", 
      hasVideo: false,
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 7, 
      title: "Still Not Over You", 
      duration: "3:15", 
      hasVideo: true,
      youtubeUrl: "https://youtu.be/50kZ-41OyG0",
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    },
    { 
      number: 8, 
      title: "Hunter's Song / Said I'm Sorry", 
      duration: "4:10", 
      hasVideo: false,
      spotify: "https://open.spotify.com/artist/thiefofjoy",
      appleMusic: "https://music.apple.com/us/album/thief-of-joy/1871049363"
    }
  ];

  const socialLinks = {
    instagram: "https://www.instagram.com/Thiefofjoymusic/",
    youtube: "https://www.youtube.com/@ThiefofJoyMusic",
    youtubeMusic: "https://music.youtube.com/watch?v=IYLuPQDIDK8",
    facebook: "https://www.facebook.com/people/Thief-Of-Joy/100090270007845/",
    pandora: "https://www.pandora.com/artist/thief-of-joy/honeybee/honeybee/TRw495z4bJkm7lk",
    linktree: "https://linktr.ee/thiefofjoy"
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <motion.div
          className="loading-logo"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <img src="/mainlogo.png" alt="Thief of Joy" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="toj-container" ref={containerRef}>
      {/* Progress Bar */}
      <motion.div className="progress-bar" style={{ width: progressWidth }} />

      {/* Navigation */}
      <motion.nav 
        className="toj-nav"
        initial={{ y: 0 }}
        animate={{ y: navVisible ? 0 : -100 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="nav-logo">
          <img src="/mainlogo.png" alt="Thief of Joy" />
        </div>
        <div className="nav-links">
          <a href="#album">Album</a>
          <a href="#videos">Videos</a>
          <a href="#tracklist">Tracklist</a>
          <a href="https://thiefofjoy.bandcamp.com/album/thief-of-joy" target="_blank" rel="noopener noreferrer" className="nav-cta">Pre-Order</a>
        </div>
      </motion.nav>

      {/* Fixed Title Under Nav */}
      <motion.div 
        className="fixed-title-container"
        style={{ opacity: fixedTitleOpacity }}
      >
        <h1 className="fixed-title">THIEF OF JOY</h1>
      </motion.div>

      {/* Animated Hero Copy Overlays - Fade in/out throughout scroll */}
      <div className="hero-copy-overlays">
        <motion.div className="hero-copy-line" style={{ opacity: heroLine1Opacity }}>
          <p>Eight tracks. Zero compromise.</p>
        </motion.div>
        <motion.div className="hero-copy-line" style={{ opacity: heroLine2Opacity }}>
          <p>Raw. Real. Relentless.</p>
        </motion.div>
        <motion.div className="hero-copy-line" style={{ opacity: heroLine3Opacity }}>
          <p>Born in late-night sessions,</p>
        </motion.div>
        <motion.div className="hero-copy-line" style={{ opacity: heroLine4Opacity }}>
          <p>forged through countless revisions,</p>
        </motion.div>
        <motion.div className="hero-copy-line" style={{ opacity: heroLine5Opacity }}>
          <p>and delivered FROM the PINE</p>
        </motion.div>
        <motion.div className="hero-copy-line" style={{ opacity: heroLine6Opacity }}>
          <p>with everything we've got—</p>
        </motion.div>
        <motion.div className="hero-copy-line" style={{ opacity: heroLine7Opacity }}>
          <p>this is THIEF OF JOY.</p>
        </motion.div>
      </div>

      {/* HERO SECTION - Video Only */}
      <section className="hero-section" ref={heroRef}>
        {/* Hero Video */}
        <motion.div 
          className="hero-video-container"
          style={{
            borderRadius: videoBorderRadius,
            scale: videoScale
          }}
        >
          <iframe
            src="https://www.youtube.com/embed/50kZ-41OyG0?autoplay=1&mute=1&loop=1&playlist=50kZ-41OyG0&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1&start=6"
            title="Thief of Joy - Album Hype"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="hero-video"
          />
        </motion.div>
      </section>

      {/* ALBUM ARTWORK SECTION */}
      <section 
        className="album-section with-band-photo" 
        id="album"
        style={{
          backgroundImage: `url(${bandPhotoBg})`
        }}
      >
        <div className="album-content">
          <motion.div
            className="album-artwork"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <img src="/Untitled_Artwork.jpg" alt="Thief of Joy Album Cover" />
          </motion.div>
          
          <motion.div
            className="album-info"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h2>THIEF OF JOY</h2>
            <h3>Self-Titled Debut Album</h3>
            <p className="album-description">
              Eight tracks of unfiltered rock and roll from Sangerville, Maine. 
              This is where it all begins.
            </p>
            <div className="album-details">
              <div className="detail">
                <span className="label">Release:</span>
                <span className="value">Coming Soon</span>
              </div>
              <div className="detail">
                <span className="label">Tracks:</span>
                <span className="value">8</span>
              </div>
              <div className="detail">
                <span className="label">Format:</span>
                <span className="value">Digital / CD / Vinyl</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PARALLAX MUSIC VIDEO CONTAINER */}
      <div className="parallax-video-wrapper" id="videos">
        
        {/* MUSIC VIDEO 1 - HONEYBEE OFFICIAL (Parallax Layer) */}
        <motion.section 
          className="video-section-parallax"
          style={{ opacity: honeybeeVideoOpacity }}
        >
          <div className="video-fullbleed-container">
            <iframe
              src="https://www.youtube.com/embed/IYLuPQDIDK8?autoplay=1&mute=1&loop=1&playlist=IYLuPQDIDK8&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1"
              title="Honeybee - Official Music Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="fullbleed-video honeybee-official-video"
            />

            <motion.div 
              className="video-overlay"
              style={{ opacity: honeybeeTextOpacity }}
            >
              <div className="video-info">
                <h3 className="video-track">HONEYBEE</h3>
                <p className="video-subtitle">Official Music Video</p>
                <p className="video-description">#3 Most Played Rock Radio Hit - Summer 2023</p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* MUSIC VIDEO 2 - HONEYBEE STUDIO (Parallax Layer) */}
        <motion.section 
          className="video-section-parallax"
          style={{ opacity: studioVideoOpacity }}
        >
          <div className="video-fullbleed-container">
            <iframe
              src="https://www.youtube.com/embed/p_OdAacF_0A?autoplay=1&mute=1&loop=1&playlist=p_OdAacF_0A&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1"
              title="Honeybee - Studio Version"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="fullbleed-video honeybee-studio-video"
            />

            <motion.div 
              className="video-overlay"
              style={{ opacity: studioTextOpacity }}
            >
              <div className="video-info">
                <h3 className="video-track">HONEYBEE</h3>
                <p className="video-subtitle">Studio Version</p>
                <p className="video-description">Stream now on all platforms</p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* SPACER to allow scrolling through videos */}
        <div className="video-scroll-spacer"></div>
      </div>

      {/* TRACKLIST SECTION */}
      <section 
        className="tracklist-section" 
        id="tracklist"
        style={{
          backgroundImage: `url(${tracklistBg})`
        }}
      >
        <motion.div
          className="tracklist-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="tracklist-title">TRACKLIST</h2>
          <div className="tracklist">
            {tracklist.map((track, index) => (
              <motion.div
                key={track.number}
                className="track"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ x: 10, backgroundColor: 'rgba(212, 165, 116, 0.1)' }}
              >
                <span className="track-number">{track.number.toString().padStart(2, '0')}</span>
                <span className="track-title">{track.title}</span>
                <span className="track-duration">{track.duration}</span>
                <div className="track-icons">
                  {/* YouTube icon for tracks with music videos */}
                  {track.hasVideo && (
                    <a href={track.youtubeUrl} target="_blank" rel="noopener noreferrer" aria-label="Watch on YouTube" className="track-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </a>
                  )}
                  {/* Spotify icon for all tracks */}
                  <a href={track.spotify} target="_blank" rel="noopener noreferrer" aria-label="Listen on Spotify" className="track-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                    </svg>
                  </a>
                  {/* Apple Music icon for all tracks */}
                  <a href={track.appleMusic} target="_blank" rel="noopener noreferrer" aria-label="Listen on Apple Music" className="track-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M23.994 6.124a9.23 9.23 0 0 0-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 0 0-1.877-.726 10.496 10.496 0 0 0-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408a10.61 10.61 0 0 0-.1 1.18c0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 0 0 1.57-.1c.822-.106 1.596-.35 2.296-.81a5.046 5.046 0 0 0 1.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.045-1.773-.6-1.943-1.536a1.88 1.88 0 0 1 1.038-2.022c.323-.16.67-.25 1.018-.324.378-.082.758-.153 1.134-.24.274-.063.457-.23.51-.516a.904.904 0 0 0 .02-.193c0-1.815 0-3.63-.002-5.443a.725.725 0 0 0-.026-.185c-.04-.15-.15-.243-.304-.234-.16.01-.318.035-.475.066l-5.597 1.09c-.113.022-.227.045-.336.09-.288.118-.43.355-.433.66v.89l.001 6.08c0 .12 0 .24-.013.358-.05.448-.24.834-.596 1.118a2.12 2.12 0 0 1-.99.444c-.653.088-1.29.02-1.897-.288-.49-.248-.86-.617-1.05-1.14a1.876 1.876 0 0 1 1.003-2.318c.324-.163.67-.256 1.02-.33.376-.08.754-.15 1.13-.237.274-.063.456-.23.51-.516a.88.88 0 0 0 .02-.192V6.37c0-.457.213-.78.64-.9l6.518-1.273c.067-.013.135-.024.202-.034.124-.018.25-.027.373.012.117.037.18.127.2.244.01.063.013.127.013.19v5.507z"/>
                    </svg>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="tracklist-total">
            <span>Total Runtime:</span>
            <span>30:37</span>
          </div>
        </motion.div>
      </section>

      {/* THE HOLLOWS IMAGE SECTION */}
      <section className="hollows-image-section">
        <motion.div
          className="hollows-image-container"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
          viewport={{ once: true }}
        >
          <img src="/trytesthollows.jpg" alt="The Hollows" className="hollows-image" />
        </motion.div>
      </section>

      {/* PRE-ORDER / CTA SECTION */}
      <section 
        className="preorder-section with-hands-bg" 
        id="preorder"
        style={{
          backgroundImage: `url(${handsInvertBg})`
        }}
      >
        <motion.div
          className="preorder-container"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="preorder-title">GET THE ALBUM</h2>
          <p className="preorder-subtitle">Pre-order now and be the first to hear it</p>
          
          <div style={{ maxWidth: '400px', margin: '0 auto', padding: '40px 0', position: 'relative', zIndex: '9999' }}>
            <div style={{ 
              backgroundColor: 'rgba(217, 164, 65, 0.1)', 
              border: '2px solid #D9A441',
              borderRadius: '8px',
              padding: '40px 30px',
              textAlign: 'center',
              position: 'relative',
              zIndex: '9999'
            }}>
              <h3 style={{ 
                fontSize: '28px', 
                fontWeight: '700', 
                letterSpacing: '2px',
                marginBottom: '15px',
                color: 'white',
                textTransform: 'uppercase'
              }}>DIGITAL</h3>
              <p style={{ 
                fontSize: '36px', 
                fontWeight: '900',
                color: '#D9A441',
                marginBottom: '25px'
              }}>$10.99</p>
              <a 
                href="https://thiefofjoy.bandcamp.com/album/thief-of-joy" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#D9A441',
                  color: '#0a0a0a',
                  padding: '15px 40px',
                  fontSize: '14px',
                  fontWeight: '700',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  zIndex: '10000',
                  pointerEvents: 'auto'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#ffffff'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#D9A441'}
              >
                PRE-ORDER
              </a>
            </div>
          </div>

          <div className="stay-connected">
            <p>Follow us on social media to stay updated on the album release</p>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="toj-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/mainlogo.png" alt="Thief of Joy" />
          </div>
          
          <div className="footer-social">
            <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5a4.25 4.25 0 0 0 4.25 4.25h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5a4.25 4.25 0 0 0-4.25-4.25h-8.5zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 1.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zm5.25-2.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
            </a>
            <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            <a href={socialLinks.youtubeMusic} target="_blank" rel="noopener noreferrer" aria-label="YouTube Music">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-11.52c-2.43 0-4.416 1.986-4.416 4.416S9.57 16.416 12 16.416s4.416-1.986 4.416-4.416S14.43 7.584 12 7.584zm-1.44 6.36V10.056L13.92 12l-3.36 1.944z"/>
              </svg>
            </a>
            <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href={socialLinks.pandora} target="_blank" rel="noopener noreferrer" aria-label="Pandora">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.5 6h3.75c2.9 0 5.25 2.35 5.25 5.25S17.15 16.5 14.25 16.5H13.5v1.5h-3V6zm3 7.5h.75c1.243 0 2.25-1.007 2.25-2.25S15.493 9 14.25 9h-.75v4.5z"/>
              </svg>
            </a>
            <a href={socialLinks.linktree} target="_blank" rel="noopener noreferrer" aria-label="Linktree">
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="m13.736 5.853 4.005-4.117 2.325 2.38-4.2 4.005h5.908v3.305h-5.937l4.229 4.108-2.325 2.334-5.74-5.769-5.741 5.769-2.325-2.325 4.229-4.108H2.226V8.121h5.909l-4.2-4.004 2.324-2.381 4.005 4.117V0h3.472zm-3.472 10.306h3.472V24h-3.472z"/>
              </svg>
            </a>
          </div>
          
          <div className="footer-info">
            <p>Thief of Joy - Sangerville, Maine</p>
            <p>© 2025 Thief of Joy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ThiefOfJoySplash;

